import {
  IonButtons,
  IonModal,
  IonTitle,
  IonToolbar,
  IonHeader,
  IonContent,
  IonItem,
  IonLabel,
  useIonToast,
  IonButton,
  IonText,
} from '@ionic/react';
import HStack from 'components/HStack';
import _ from 'lodash';
import { SnapshotOut } from 'mobx-state-tree';
import { UserSN } from 'models/User';
import {
  ComponentProps,
  FunctionComponent,
  useEffect,
  useRef,
  useState,
  useCallback,
  Dispatch,
  SetStateAction,
} from 'react';
import { useTranslation } from 'react-i18next';
import Trans from 'util/Trans';
import DynamicButton from 'components/buttons/DynamicButton';
import { useUnitConversion } from 'hooks/useUnitConversion';
import Icon from 'components/icons/Icon';
import ProfileTopBar from 'pages/Profile/components/ProfileTopBar';
import { useStores } from '../../models';
import Spacer from 'components/Spacer';
import { MESSAGE_ALIVE_MS, MESSAGE_POSITION } from 'util/constants';

export interface RiderEditModalProps extends ComponentProps<typeof IonModal> {
  onCommit: () => Promise<void>;
  user?: SnapshotOut<UserSN>;
  // Setter aus der Profilseite, um editUser zu aktualisieren (z.B. photoURL nach Upload)
  setUser?: Dispatch<SetStateAction<SnapshotOut<UserSN> | undefined>>;
}

interface FormData {
  realName: string;
  username: string;
  email: string;
  phoneNumber: string;
  license: string;
  birthday: string;
  nationality: string;
  gender: string;
  street: string;
  city: string;
  zip: string;
  country: string;
  stance: string;
  height: string;
  heightFeet: string;
  heightInches: string;
  weight: string;
}

interface ValidationErrors {
  realName?: string;
  username?: string;
  email?: string;
  license?: string;
  nationality?: string;
  gender?: string;
  street?: string;
  city?: string;
  zip?: string;
  country?: string;
  birthday?: string;
  stance?: string;
  height?: string;
  heightFeet?: string;
  heightInches?: string;
  weight?: string;
}

const checkIfValid = (formData?: FormData): boolean => {
  return !!(
    formData &&
    formData.email &&
    formData.realName &&
    formData.username &&
    formData.city &&
    formData.country &&
    formData.zip &&
    formData.street
  );
};

const RiderEditModal: FunctionComponent<RiderEditModalProps> = ({
  onCommit,
  user,
  setUser,
  ...rest
}) => {
  const modalRef = useRef<HTMLIonModalElement>(null);
  const [isCommitting, setIsCommitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>(
    {}
  );
  const { t } = useTranslation();
  const unitConversion = useUnitConversion();
  const [present] = useIonToast();

  // Initialisiere FormData aus User-Model (inkl. address/bodyInfo)
  function initFormData(): FormData {
    let heightStr = '';
    let heightFeetStr = '';
    let heightInchesStr = '';
    if (user?.bodyInfo?.height) {
      if (unitConversion.userUnits === 'metric') {
        heightStr = user.bodyInfo.height.toString();
      } else {
        const heightInInches = unitConversion.convert(
          user.bodyInfo.height,
          'height',
          'metric',
          'imperial'
        );
        const feet = Math.floor(heightInInches / 12);
        const inches = Math.round(heightInInches % 12);
        heightFeetStr = feet.toString();
        heightInchesStr = inches.toString();
      }
    }
    return {
      realName: user?.realName || '',
      username: user?.displayName || '',
      email: user?.email || '',
      phoneNumber: user?.phoneNumber || '',
      license: user?.license || '',
      birthday: user?.birthday || '',
      nationality: user?.nationality || '',
      gender: user?.gender || '',
      street: user?.address?.street || '',
      city: user?.address?.city || '',
      zip: user?.address?.zip || '',
      country: user?.address?.country || '',
      stance: user?.bodyInfo?.stance || '',
      height: heightStr,
      heightFeet: heightFeetStr,
      heightInches: heightInchesStr,
      weight: user?.bodyInfo?.weight
        ? unitConversion.userUnits === 'metric'
          ? user.bodyInfo.weight.toString()
          : unitConversion
              .convert(user.bodyInfo.weight, 'weight', 'metric', 'imperial')
              .toString()
        : '',
    };
  }

  const [formData, setFormData] = useState<FormData>(initFormData);
  const { currentUser, recordList } = useStores();

  useEffect(() => {
    setFormData(initFormData());
  }, [user, unitConversion.userUnits]);

  const [isValidInput, setIsValidInput] = useState(() =>
    checkIfValid(formData)
  );
  useEffect(() => {
    setIsValidInput(
      checkIfValid(formData) && Object.keys(validationErrors).length === 0
    );
  }, [formData, validationErrors]);

  const dismiss = () => {
    modalRef.current?.dismiss();
  };

  // Validierung: Pflichtfeld-Logik entfernt, alle Felder sind freiwillig
  function validateField(fieldName: keyof ValidationErrors, value: string) {
    setValidationErrors((prev) => {
      const newErrors = { ...prev };
      // Keine Pflichtfeldprüfung mehr
      if (fieldName === 'email' && value.trim()) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value.trim())) {
          newErrors[fieldName] = t('INVALID_EMAIL', 'Ungültige E-Mail-Adresse');
        } else {
          delete newErrors[fieldName];
        }
      } else {
        delete newErrors[fieldName];
      }
      return newErrors;
    });
  }

  // Update User-Objekt (inkl. address/bodyInfo)
  const updateUser = (): void => {
    if (!user) return;
    const editedUser = user as any;
    editedUser.realName = formData.realName;
    editedUser.displayName = formData.username;
    editedUser.email = formData.email;
    editedUser.phoneNumber = formData.phoneNumber;
    editedUser.license = formData.license || null;
    editedUser.birthday = formData.birthday;
    editedUser.nationality = formData.nationality;
    editedUser.gender = formData.gender;
    // Address
    if (!editedUser.address) editedUser.address = {};
    editedUser.address.street = formData.street;
    editedUser.address.city = formData.city;
    editedUser.address.zip = formData.zip;
    editedUser.address.country = formData.country;
    // BodyInfo
    if (!editedUser.bodyInfo) editedUser.bodyInfo = {};
    editedUser.bodyInfo.stance = formData.stance;
    // Height/Weight: Umrechnung wie im BoardEditModal
    if (unitConversion.userUnits === 'metric') {
      editedUser.bodyInfo.height = formData.height
        ? Math.round(parseFloat(formData.height))
        : null;
      editedUser.bodyInfo.weight = formData.weight
        ? Math.round(parseFloat(formData.weight))
        : null;
    } else {
      // Imperial: feet/inches zu cm
      if (formData.heightFeet && formData.heightInches) {
        const totalInches =
          parseInt(formData.heightFeet) * 12 + parseInt(formData.heightInches);
        editedUser.bodyInfo.height = Math.round(
          unitConversion.convert(totalInches, 'height', 'imperial', 'metric')
        );
      } else {
        editedUser.bodyInfo.height = null;
      }
      editedUser.bodyInfo.weight = formData.weight
        ? Math.round(
            unitConversion.convert(
              parseFloat(formData.weight),
              'weight',
              'imperial',
              'metric'
            )
          )
        : null;
    }
  };

  const _onCommit = async () => {
    setIsCommitting(true);
    updateUser();
    onCommit().finally(() => {
      setIsCommitting(false);
    });
  };

   const handlePhotoSelected = async (file: File) => {
    if (!currentUser) {
      present({
        message: 'Profilbild-Upload fehlgeschlagen: Kein Benutzer geladen.',
        duration: MESSAGE_ALIVE_MS,
        color: 'danger',
        position: MESSAGE_POSITION,
      });
      return;
    }
    try {
      const url = await currentUser.uploadProfilePhoto(file);
      // Wenn im Edit-Modus, auch das vom Parent übergebene user (editUser) aktualisieren
      if (user && setUser) {
        setUser({ ...user, photoURL: url } as SnapshotOut<UserSN>);
      }
      present({
        message: 'Profilbild erfolgreich hochgeladen! Bald sichtbar.',
        duration: MESSAGE_ALIVE_MS,
        color: 'success',
        position: MESSAGE_POSITION,
      });
    } catch (err) {
      present({
        message: 'Fehler beim Hochladen des Profilbilds.',
        duration: MESSAGE_ALIVE_MS,
        color: 'danger',
        position: MESSAGE_POSITION,
      });
    }
  };

  if (!currentUser) return null;

  return (
    <IonModal {...rest} ref={modalRef} className="edit-modal">
      <IonHeader className="ion-no-border">
        <IonToolbar className="edit-modal-toptoolbar">
          <IonTitle style={{ textAlign: 'center' }}>
            <Trans i18nKey="EDIT_PROFILE">Profil bearbeiten</Trans>
          </IonTitle>
          <IonButtons slot="start">
            <DynamicButton
              text={<Trans i18nKey="OK" />}
              iconName="OKUpIcon"
              buttonType="icon-full"
              size="xlarge"
              fill="clear"
              className="spnx-button"
              disabled={isCommitting || !isValidInput}
              onClick={_onCommit}
            />
          </IonButtons>
          <IonButtons slot="end">
            <DynamicButton
              text={<Trans i18nKey="CANCEL" />}
              iconName="CancelUpIcon"
              buttonType="icon-full"
              size="xlarge"
              fill="clear"
              className="spnx-button"
              onClick={dismiss}
            />
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      
      <IonContent
        style={{ '--background': 'transparent', '--border-colour': 0 }}
      >
        <div className="profile-topbar-modal-wrapper">
          <ProfileTopBar
            user={currentUser}
            onclick={() => {}}
            onPhotoSelected={handlePhotoSelected}
            hasPencil={true}
          />
        </div>
        <div style={{ marginBottom: 16 }} />
        <Spacer />
        <div className="edit-modal-wrapper">
          <div className="edit-modal-group">
            <IonText>
              <Trans i18nKey="PERSONAL_INFO">Persönliche Daten</Trans>
            </IonText>
            <div className="edit-modal-innergroup">
              {/* Name */}
              <IonItem>
                <IonLabel position="stacked">
                  <Trans i18nKey="NAME" />
                </IonLabel>
                <input
                  type="text"
                  value={formData.realName}
                  onChange={(e) => {
                    setFormData((prev) => ({
                      ...prev,
                      realName: e.target.value,
                    }));
                    validateField('realName', e.target.value);
                  }}
                  placeholder="Name"
                />
                {validationErrors.realName && (
                  <span className="error-note">
                    {validationErrors.realName}
                  </span>
                )}
              </IonItem>
              {/* Username */}
              <IonItem>
                <IonLabel position="stacked">
                  <Trans i18nKey="USERNAME" />
                </IonLabel>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => {
                    setFormData((prev) => ({
                      ...prev,
                      username: e.target.value,
                    }));
                    validateField('username', e.target.value);
                  }}
                  placeholder="Benutzername"
                />
                {validationErrors.username && (
                  <span className="error-note">
                    {validationErrors.username}
                  </span>
                )}
              </IonItem>
              {/* Email */}
              <IonItem>
                <IonLabel position="stacked">
                  <Trans i18nKey="EMAIL" />
                </IonLabel>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => {
                    setFormData((prev) => ({ ...prev, email: e.target.value }));
                    validateField('email', e.target.value);
                  }}
                  placeholder="E-Mail"
                />
                {validationErrors.email && (
                  <span className="error-note">{validationErrors.email}</span>
                )}
              </IonItem>
              {/* Phone */}
              <IonItem>
                <IonLabel position="stacked">
                  <Trans i18nKey="PHONE" />
                </IonLabel>
                <input
                  type="tel"
                  value={formData.phoneNumber}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      phoneNumber: e.target.value,
                    }))
                  }
                  placeholder="Telefonnummer"
                />
              </IonItem>
              {/* Birthday */}
              <IonItem>
                <IonLabel position="stacked">
                  <Trans i18nKey="BIRTHDAY" />
                </IonLabel>
                <input
                  type="date"
                  value={formData.birthday}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      birthday: e.target.value,
                    }))
                  }
                  placeholder="Geburtstag"
                />
              </IonItem>
              {/* Nationality */}
              <IonItem>
                <IonLabel position="stacked">
                  <Trans i18nKey="NATIONALITY" />
                  <span style={{ paddingLeft: 8 }}>
                    <Icon name="NationalityIcon" size="small" />
                  </span>
                </IonLabel>
                <input
                  type="text"
                  value={formData.nationality}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      nationality: e.target.value,
                    }))
                  }
                  placeholder="Nationalität"
                />
              </IonItem>
              {/* Gender */}
              <IonItem>
                <IonLabel position="stacked">
                  <Trans i18nKey="GENDER" />
                </IonLabel>
                <select
                  value={formData.gender}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, gender: e.target.value }))
                  }
                >
                  <option value="">-</option>
                  <option value="male">
                    <Trans i18nKey="MALE">Männlich</Trans>
                  </option>
                  <option value="female">
                    <Trans i18nKey="FEMALE">Weiblich</Trans>
                  </option>
                  <option value="diverse">
                    <Trans i18nKey="DIVERS">Divers</Trans>
                  </option>
                  <option value="all">
                    <Trans i18nKey="ALL">All</Trans>
                  </option>
                </select>
              </IonItem>
            </div>
          </div>
          <div className="edit-modal-group">
            <IonText>
              <Trans i18nKey="ADDRESS">Adresse</Trans>
            </IonText>
            <div className="edit-modal-innergroup">
              {/* Street */}
              <IonItem>
                <IonLabel position="stacked">
                  <Trans i18nKey="STREET" />
                  <span style={{ paddingLeft: 8 }}>
                    <Icon name="WorldIcon" size="small" />
                  </span>
                </IonLabel>
                <input
                  type="text"
                  value={formData.street}
                  onChange={(e) => {
                    setFormData((prev) => ({
                      ...prev,
                      street: e.target.value,
                    }));
                    validateField('street', e.target.value);
                  }}
                  placeholder="Straße"
                />
                {validationErrors.street && (
                  <span className="error-note">{validationErrors.street}</span>
                )}
              </IonItem>
              {/* City */}
              <IonItem>
                <IonLabel position="stacked">
                  <Trans i18nKey="CITY" />
                  <span style={{ paddingLeft: 8 }}>
                    <Icon name="WorldIcon" size="small" />
                  </span>
                </IonLabel>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => {
                    setFormData((prev) => ({ ...prev, city: e.target.value }));
                    validateField('city', e.target.value);
                  }}
                  placeholder="Stadt"
                />
                {validationErrors.city && (
                  <span className="error-note">{validationErrors.city}</span>
                )}
              </IonItem>
              {/* ZIP */}
              <IonItem>
                <IonLabel position="stacked">
                  <Trans i18nKey="ZIP" />
                  <span style={{ paddingLeft: 8 }}>
                    <Icon name="WorldIcon" size="small" />
                  </span>
                </IonLabel>
                <input
                  type="text"
                  value={formData.zip}
                  onChange={(e) => {
                    setFormData((prev) => ({ ...prev, zip: e.target.value }));
                    validateField('zip', e.target.value);
                  }}
                  placeholder="PLZ"
                />
                {validationErrors.zip && (
                  <span className="error-note">{validationErrors.zip}</span>
                )}
              </IonItem>
              {/* Country */}
              <IonItem>
                <IonLabel position="stacked">
                  <Trans i18nKey="COUNTRY" />
                  <span style={{ paddingLeft: 8 }}>
                    <Icon name="WorldIcon" size="small" />
                  </span>
                </IonLabel>
                <input
                  type="text"
                  value={formData.country}
                  onChange={(e) => {
                    setFormData((prev) => ({
                      ...prev,
                      country: e.target.value,
                    }));
                    validateField('country', e.target.value);
                  }}
                  placeholder="Land"
                />
                {validationErrors.country && (
                  <span className="error-note">{validationErrors.country}</span>
                )}
              </IonItem>
            </div>
          </div>
          <div className="edit-modal-group">
            <IonText>
              <Trans i18nKey="BODY_INFO">Körperdaten</Trans>
            </IonText>
            <div className="edit-modal-innergroup">
              {/* Stance */}
              <IonItem>
                <IonLabel position="stacked">
                  <Trans i18nKey="STANCE" />
                </IonLabel>
                <select
                  value={formData.stance}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, stance: e.target.value }))
                  }
                >
                  <option value="">-</option>
                  <option value="goofy">Goofy</option>
                  <option value="regular">Regular</option>
                </select>
              </IonItem>
              {/* Height */}
              {unitConversion.userUnits === 'metric' ? (
                <IonItem>
                  <IonLabel position="stacked">
                    <Trans i18nKey="HEIGHT" />
                  </IonLabel>
                  <input
                    type="number"
                    value={formData.height}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        height: e.target.value,
                      }))
                    }
                    placeholder="Größe in cm"
                    step="1"
                  />
                </IonItem>
              ) : (
                <HStack>
                  <IonItem>
                    <IonLabel position="stacked">
                      <Trans i18nKey="HEIGHT_FEET" />
                    </IonLabel>
                    <input
                      type="number"
                      value={formData.heightFeet}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          heightFeet: e.target.value,
                        }))
                      }
                      placeholder="Fuß"
                      step="1"
                    />
                  </IonItem>
                  <IonItem>
                    <IonLabel position="stacked">
                      <Trans i18nKey="HEIGHT_INCHES" />
                    </IonLabel>
                    <input
                      type="number"
                      value={formData.heightInches}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          heightInches: e.target.value,
                        }))
                      }
                      placeholder="Zoll"
                      step="1"
                    />
                  </IonItem>
                </HStack>
              )}
              {/* Weight */}
              <IonItem>
                <IonLabel position="stacked">
                  <Trans i18nKey="WEIGHT" />
                </IonLabel>
                <input
                  type="number"
                  value={formData.weight}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, weight: e.target.value }))
                  }
                  placeholder={`Gewicht in ${unitConversion.getUnitLabel('weight')}`}
                  step="1"
                />
              </IonItem>
            </div>
          </div>
          <div className="edit-modal-group">
            <IonText>
              <Trans i18nKey="LICENSE">Lizenz</Trans>
            </IonText>
            <div className="edit-modal-innergroup">
              <IonItem>
                <IonLabel position="stacked">
                  <Trans i18nKey="LICENSE_KEY" />
                </IonLabel>
                <input
                  type="text"
                  value={formData.license}
                  onChange={(e) => {
                    setFormData((prev) => ({
                      ...prev,
                      license: e.target.value,
                    }));
                    // Optional: Lizenz-Validierung
                  }}
                  placeholder="XX-XX-XX-XX-XX-XX"
                />
                {validationErrors.license && (
                  <span className="error-note">{validationErrors.license}</span>
                )}
              </IonItem>
            </div>
          </div>
        </div>
      </IonContent>
    </IonModal>
  );
};

export default RiderEditModal;
